import { displayDALocation } from '../lib/daDisplay';
import { useEffect, useMemo, useState } from 'react';
import { GMAP_KEY } from '../lib/constants';
import {
  fetchPublicFirmAudit,
  formatAuditMoney,
  prettyRole,
  recordFirmAuditEvent,
  shortCouncil,
} from '../lib/firmAudit';

const palette = {
  ink: '#101820',
  muted: '#52606f',
  amber: '#c79b3b',
  amberDark: '#8a661f',
  paper: '#faf7f1',
  panel: '#ffffff',
  line: '#e8dfd1',
  blue: '#315a7d',
  green: '#286f55',
  rust: '#a95d38',
};

function parseFirmRef() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return decodeURIComponent(parts[1] || '');
}

function pct(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / total) * 100);
}

function formatDate(value) {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function staticMapUrl(markers = []) {
  if (!GMAP_KEY || !markers.length) return '';
  const valid = markers
    .filter(m => Number.isFinite(Number(m.latitude)) && Number.isFinite(Number(m.longitude)))
    .slice(0, 12);
  if (!valid.length) return '';

  const avgLat = valid.reduce((sum, m) => sum + Number(m.latitude), 0) / valid.length;
  const avgLng = valid.reduce((sum, m) => sum + Number(m.longitude), 0) / valid.length;
  const params = new URLSearchParams({
    center: `${avgLat},${avgLng}`,
    zoom: valid.length === 1 ? '14' : '10',
    size: '1200x700',
    scale: '2',
    maptype: 'roadmap',
    key: GMAP_KEY,
  });
  for (const marker of valid) {
    params.append('markers', `color:0xc79b3b|${marker.latitude},${marker.longitude}`);
  }
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

function ensureNoIndexMeta() {
  const existing = document.querySelector('meta[name="robots"][data-sitescope-route="firm-audit"]');
  if (existing) {
    existing.setAttribute('content', 'noindex,follow');
    return existing;
  }
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'robots');
  meta.setAttribute('content', 'noindex,follow');
  meta.setAttribute('data-sitescope-route', 'firm-audit');
  document.head.appendChild(meta);
  return meta;
}

function Stat({ label, value, tone = palette.ink }) {
  return (
    <div className="audit-stat">
      <div className="audit-stat-value" style={{ color: tone }}>{value}</div>
      <div className="audit-stat-label">{label}</div>
    </div>
  );
}

function BarRow({ label, count, total, tone = palette.amber }) {
  const width = Math.max(4, pct(count, total));
  return (
    <div className="audit-bar-row">
      <div className="audit-bar-meta">
        <span>{label}</span>
        <strong>{count}</strong>
      </div>
      <div className="audit-bar-track">
        <div className="audit-bar-fill" style={{ width: `${width}%`, background: tone }} />
      </div>
    </div>
  );
}

export default function FirmAuditPage() {
  const firmRef = parseFirmRef();
  const [audit, setAudit] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    const meta = ensureNoIndexMeta();
    return () => {
      if (meta.parentNode) {
        meta.parentNode.removeChild(meta);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetchPublicFirmAudit(firmRef)
      .then(data => {
        if (cancelled) return;
        setAudit(data);
        setState(data?.found ? 'ready' : 'not_found');
        if (data?.found && data?.firm?.id) {
          recordFirmAuditEvent(data.firm.id, 'view', {
            path: window.location.pathname,
            ref: firmRef,
          });
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[firm audit] load failed', err);
        setState('error');
      });
    return () => { cancelled = true; };
  }, [firmRef]);

  const mapUrl = useMemo(() => staticMapUrl(audit?.map_markers || []), [audit]);
  const firm = audit?.firm || {};
  const summary = audit?.summary || {};
  const total = Number(summary.active_da_count ?? summary.da_count ?? 0);
  const pipelineValue = summary.active_pipeline_value ?? summary.pipeline_value;
  const mappedCount = Number(summary.active_mapped_count ?? summary.mapped_count ?? 0);
  const firmAuditRef = firm.audit_ref || firm.slug || firm.id || '';
  const signupHref = `/?source=audit&firm_id=${encodeURIComponent(firmAuditRef)}`;

  const handleSignupClick = () => {
    if (firm.id) {
      localStorage.setItem('ss_audit_signup_context', JSON.stringify({
        firm_id: firm.id,
        firm_ref: firmAuditRef,
        firm_name: firm.name,
        councils: audit?.councils || [],
        created_at: new Date().toISOString(),
      }));
      recordFirmAuditEvent(firm.id, 'cta_click', { source: 'public_audit_page' });
    }
  };

  if (state === 'loading') {
    return (
      <div className="firm-audit-page audit-loading">
        <style>{styles}</style>
        <div className="audit-loader" />
      </div>
    );
  }

  if (state !== 'ready') {
    const isError = state === 'error';
    return (
      <div className="firm-audit-page audit-empty">
        <style>{styles}</style>
        <div>
          <div className="audit-kicker">SiteScope</div>
          <h1>{isError ? 'Flyer temporarily unavailable' : 'Flyer not found'}</h1>
          <p>
            {isError
              ? 'This 1 Click Flyer could not be loaded. Please try again shortly.'
              : 'This 1 Click Flyer link is unavailable or has not been generated yet.'}
          </p>
          {firmRef && <p className="audit-ref">Reference: {firmRef}</p>}
          <a className="audit-button" href="/">Open SiteScope</a>
        </div>
      </div>
    );
  }

  return (
    <div className="firm-audit-page">
      <style>{styles}</style>

      <section className="audit-hero" style={{ backgroundImage: mapUrl ? `linear-gradient(90deg, rgba(16,24,32,0.92), rgba(16,24,32,0.62), rgba(16,24,32,0.18)), url(${mapUrl})` : undefined }}>
        <div className="audit-shell audit-hero-inner">
          <div className="audit-brand">SiteScope</div>
          <div className="audit-kicker">Northern Rivers active DA footprint</div>
          <h1>{firm.name}</h1>
          <p>
            SiteScope found {total} currently active DA{total === 1 ? '' : 's'} linked to {firm.name}
            {audit.councils?.length ? ` across ${audit.councils.map(c => shortCouncil(c.council)).join(', ')}` : ''}.
          </p>
          <div className="audit-hero-actions">
            <a className="audit-button" href={signupHref} onClick={handleSignupClick}>Secure founder discount</a>
            <a className="audit-ghost" href="#recent">View recent activity</a>
          </div>
        </div>
      </section>

      <main className="audit-shell">
        <section className="audit-stats-band" aria-label="Firm DA summary">
          <Stat label="Active DA records" value={total.toLocaleString('en-AU')} tone={palette.amberDark} />
          <Stat label="Active pipeline" value={formatAuditMoney(pipelineValue)} tone={palette.blue} />
          <Stat label="Mapped active DAs" value={`${mappedCount.toLocaleString('en-AU')}`} tone={palette.green} />
          <Stat label="Primary role" value={prettyRole(firm.primary_role)} tone={palette.rust} />
        </section>

        <section className="audit-grid">
          <div className="audit-panel">
            <div className="audit-section-head">
              <span>Coverage</span>
              <strong>{audit.councils?.length || 0} active councils</strong>
            </div>
            {(audit.councils || []).map((row, idx) => (
              <BarRow
                key={row.council}
                label={shortCouncil(row.council)}
                count={row.count}
                total={total}
                tone={[palette.amber, palette.blue, palette.green, palette.rust][idx % 4]}
              />
            ))}
          </div>

          <div className="audit-panel">
            <div className="audit-section-head">
              <span>Status Mix</span>
              <strong>Active as at {formatDate(summary.latest_lodged_date)}</strong>
            </div>
            {(audit.statuses || []).slice(0, 6).map((row, idx) => (
              <BarRow
                key={row.status}
                label={row.status}
                count={row.count}
                total={total}
                tone={[palette.blue, palette.amber, palette.green, palette.rust][idx % 4]}
              />
            ))}
          </div>
        </section>

        <section className="audit-two-col">
          <div>
            <div className="audit-kicker dark">Roles found</div>
            <h2>{firm.name} appears most often as {prettyRole(firm.primary_role).toLowerCase()} on active DAs.</h2>
          </div>
          <div className="audit-role-list">
            {(firm.roles || []).map(role => (
              <div className="audit-role" key={role.role}>
                <span>{prettyRole(role.role)}</span>
                <strong>{role.count}</strong>
              </div>
            ))}
          </div>
        </section>

        <section id="recent" className="audit-recent">
          <div className="audit-section-head wide">
            <span>Current DA Activity</span>
            <strong>{firm.abn ? `ABN ${firm.abn}` : 'Public DA records'}</strong>
          </div>
          {(audit.recent_das || []).map(da => (
            <article className="audit-da" key={da.id}>
              <div>
                <div className="audit-da-number">{da.da_number || 'DA record'}</div>
                <h3>{displayDALocation(da, 'Street address not provided')}</h3>
                <p>{da.description || 'Development application activity recorded in SiteScope.'}</p>
              </div>
              <div className="audit-da-meta">
                <span>{shortCouncil(da.council)}</span>
                <span>{da.status || 'Unknown status'}</span>
                <span>{formatDate(da.lodged_date)}</span>
                <strong>{formatAuditMoney(da.value)}</strong>
              </div>
            </article>
          ))}
        </section>

        <section className="audit-cta">
          <div>
            <div className="audit-kicker dark">See It First</div>
            <h2>Track new DAs, map activity, and watch the areas that matter to your team.</h2>
          </div>
          <a className="audit-button dark" href={signupHref} onClick={handleSignupClick}>Secure founder discount</a>
        </section>
      </main>
    </div>
  );
}

const styles = `
.firm-audit-page {
  min-height: 100vh;
  background: ${palette.paper};
  color: ${palette.ink};
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}
.audit-shell {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
}
.audit-hero {
  min-height: 68vh;
  background: ${palette.ink};
  background-size: cover;
  background-position: center;
  color: #fff;
  display: flex;
  align-items: center;
}
.audit-hero-inner {
  padding: 58px 0 72px;
}
.audit-brand {
  font-family: Georgia, serif;
  font-size: 22px;
  font-weight: 800;
  color: #f0c56b;
  margin-bottom: 42px;
}
.audit-kicker {
  color: #f0c56b;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0;
  margin-bottom: 12px;
}
.audit-kicker.dark {
  color: ${palette.amberDark};
}
.audit-hero h1 {
  font-family: Georgia, serif;
  font-size: clamp(42px, 8vw, 86px);
  line-height: 0.98;
  letter-spacing: 0;
  margin: 0 0 18px;
  max-width: 850px;
}
.audit-hero p {
  max-width: 660px;
  font-size: 19px;
  line-height: 1.55;
  color: rgba(255,255,255,0.86);
  margin: 0;
}
.audit-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 30px;
}
.audit-button,
.audit-ghost {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 900;
  font-size: 14px;
  text-decoration: none;
}
.audit-button {
  background: #f0c56b;
  color: ${palette.ink};
  border: 1px solid #f0c56b;
}
.audit-button.dark {
  background: ${palette.ink};
  border-color: ${palette.ink};
  color: #fff;
}
.audit-ghost {
  color: #fff;
  border: 1px solid rgba(255,255,255,0.38);
}
.audit-stats-band {
  margin-top: -38px;
  background: ${palette.panel};
  border: 1px solid ${palette.line};
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  box-shadow: 0 18px 42px rgba(16,24,32,0.12);
}
.audit-stat {
  min-height: 128px;
  padding: 24px;
  border-right: 1px solid ${palette.line};
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.audit-stat:last-child {
  border-right: 0;
}
.audit-stat-value {
  font-family: Georgia, serif;
  font-size: clamp(26px, 4vw, 40px);
  font-weight: 800;
  line-height: 1;
}
.audit-stat-label {
  color: ${palette.muted};
  font-size: 13px;
  font-weight: 800;
  margin-top: 8px;
}
.audit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin: 34px 0;
}
.audit-panel,
.audit-recent,
.audit-cta {
  background: ${palette.panel};
  border: 1px solid ${palette.line};
}
.audit-panel {
  padding: 22px;
}
.audit-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.audit-section-head span {
  font-family: Georgia, serif;
  font-size: 24px;
  font-weight: 800;
}
.audit-section-head strong {
  color: ${palette.muted};
  font-size: 13px;
}
.audit-bar-row + .audit-bar-row {
  margin-top: 16px;
}
.audit-bar-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  font-weight: 800;
}
.audit-bar-track {
  height: 9px;
  background: #efe7da;
  margin-top: 8px;
  overflow: hidden;
}
.audit-bar-fill {
  height: 100%;
}
.audit-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 42px;
  align-items: start;
  padding: 44px 0;
}
.audit-two-col h2,
.audit-cta h2 {
  font-family: Georgia, serif;
  font-size: clamp(30px, 4vw, 48px);
  line-height: 1.08;
  letter-spacing: 0;
  margin: 0;
}
.audit-role-list {
  display: grid;
  gap: 10px;
}
.audit-role {
  background: #fff;
  border: 1px solid ${palette.line};
  padding: 16px 18px;
  display: flex;
  justify-content: space-between;
  gap: 18px;
  font-weight: 900;
}
.audit-role strong {
  color: ${palette.amberDark};
}
.audit-recent {
  padding: 24px;
}
.audit-section-head.wide {
  border-bottom: 1px solid ${palette.line};
  padding-bottom: 18px;
}
.audit-da {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 28px;
  padding: 22px 0;
  border-bottom: 1px solid ${palette.line};
}
.audit-da:last-child {
  border-bottom: 0;
}
.audit-da-number {
  color: ${palette.amberDark};
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 6px;
}
.audit-da h3 {
  font-size: 18px;
  line-height: 1.3;
  margin: 0 0 8px;
}
.audit-da p {
  color: ${palette.muted};
  line-height: 1.45;
  margin: 0;
}
.audit-da-meta {
  display: grid;
  gap: 7px;
  align-content: start;
  color: ${palette.muted};
  font-size: 13px;
  font-weight: 800;
}
.audit-da-meta strong {
  color: ${palette.ink};
  font-size: 18px;
}
.audit-cta {
  margin: 34px 0 54px;
  padding: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}
.audit-cta h2 {
  max-width: 760px;
}
.audit-empty,
.audit-loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}
.audit-empty h1 {
  font-family: Georgia, serif;
  font-size: 44px;
  margin: 0 0 10px;
}
.audit-empty p {
  color: ${palette.muted};
  margin: 0 0 22px;
}
.audit-ref {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  word-break: break-all;
  color: ${palette.muted};
  opacity: 0.72;
}
.audit-loader {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid #e8dfd1;
  border-top-color: ${palette.amber};
  animation: audit-spin 0.8s linear infinite;
}
@keyframes audit-spin { to { transform: rotate(360deg); } }
@media (max-width: 760px) {
  .audit-hero {
    min-height: 72vh;
  }
  .audit-hero-inner {
    padding: 34px 0 58px;
  }
  .audit-brand {
    margin-bottom: 34px;
  }
  .audit-stats-band,
  .audit-grid,
  .audit-two-col,
  .audit-da,
  .audit-cta {
    grid-template-columns: 1fr;
  }
  .audit-stats-band {
    margin-top: -26px;
  }
  .audit-stat {
    min-height: 96px;
    border-right: 0;
    border-bottom: 1px solid ${palette.line};
  }
  .audit-stat:last-child {
    border-bottom: 0;
  }
  .audit-two-col {
    gap: 22px;
  }
  .audit-cta {
    align-items: flex-start;
  }
}
`;
